import {
  PreviewLens, ReleaseNotePage, ReleaseNotePermission, ReleaseNotePermissionPreset
} from '../../../shared/models/release-note/release-note.model';
import { isPageVisibleTo } from './release-note-visibility.util';

/** A badge as either a translate key (with params) or literal text, such as a preset's name. */
export interface PermissionBadge {
  key?: string;
  text?: string;
  params?: { [name: string]: number };
}

/** A page's permission codes, unique and sorted, so sets compare by value. */
export function codesOf(permissions: Array<Pick<ReleaseNotePermission, 'code'>> | null | undefined): Array<string> {
  return Array.from(new Set((permissions ?? []).map(permission => permission.code).filter(Boolean))).sort((a, b) => a.localeCompare(b));
}

/** Permission code to display name, falling back to the code. */
export function permissionNames(permissions: Array<ReleaseNotePermission>): Map<string, string> {
  return new Map((permissions ?? []).map(permission => [permission.code, permission.name ?? permission.code] as [string, string]));
}

export function sameCodes(a: Array<string>, b: Array<string>): boolean {
  const left = new Set(a);
  const right = new Set(b);
  return left.size === right.size && Array.from(left).every(code => right.has(code));
}

/** The preset whose code set exactly equals `codes`, if any. */
export function matchingPreset(codes: Array<string>, presets: Array<ReleaseNotePermissionPreset>): ReleaseNotePermissionPreset | null {
  if (!codes.length) {
    return null;
  }
  return (presets ?? []).find(preset => sameCodes(codesOf(preset.permissions), codes)) ?? null;
}

/**
 * The slide-head badge (RA-2695): ALL USERS for an empty set, a preset's name on an exact match, the single
 * permission's name, else "N permissions".
 */
export function badgeFor(codes: Array<string>, presets: Array<ReleaseNotePermissionPreset>,
                         names: Map<string, string>): PermissionBadge {
  if (!codes.length) {
    return { key: 'RELEASE_NOTES.EDITOR.BADGE_ALL' };
  }
  const preset = matchingPreset(codes, presets);
  if (preset) {
    return { text: preset.name };
  }
  if (codes.length === 1) {
    return { text: names.get(codes[0]) ?? codes[0] };
  }
  return { key: 'RELEASE_NOTES.EDITOR.BADGE_N', params: { n: codes.length } };
}

/**
 * Which picker option a stored set corresponds to: `everyone`, `preset:{id}`, `perm:{code}`, or `custom` —
 * a set nothing selectable reproduces (an import with two arbitrary codes, say), shown read-only until changed.
 */
export function selectionKeyFor(codes: Array<string>, presets: Array<ReleaseNotePermissionPreset>,
                                names: Map<string, string>): string {
  if (!codes.length) {
    return 'everyone';
  }
  const preset = matchingPreset(codes, presets);
  if (preset) {
    return `preset:${preset.id}`;
  }
  return codes.length === 1 && names.has(codes[0]) ? `perm:${codes[0]}` : 'custom';
}

/**
 * The editor Preview's "Preview as" personas (RA-2870, moved out of the editor): the author, a user with no
 * special permissions, then one per distinct non-empty permission set across the pages — first-seen order,
 * de-duplicated by value — named by the preset matching it exactly, else by its permissions' names. Each label
 * carries how many pages that persona would see.
 *
 * @param translate resolves a key with params; the editor passes `TranslateService.instant`
 */
export function buildLenses(pages: Array<Pick<ReleaseNotePage, 'permissions'>> | null | undefined,
                            permissions: Array<ReleaseNotePermission>, presets: Array<ReleaseNotePermissionPreset>,
                            translate: (key: string, params: object) => string): Array<PreviewLens> {
  const all = pages ?? [];
  const count = (held: Set<string>) => all.filter(page => isPageVisibleTo(codesOf(page.permissions), held)).length;
  const names = permissionNames(permissions);
  const lenses: Array<PreviewLens> = [
    { label: translate('RELEASE_NOTES.PREVIEW.AUTHOR', { n: all.length }), held: null },
    { label: translate('RELEASE_NOTES.PREVIEW.NOBODY', { n: count(new Set()) }), held: new Set() }
  ];
  const seen = new Set<string>();
  all.map(page => codesOf(page.permissions)).filter(codes => codes.length).forEach(codes => {
    const key = codes.join('|');
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    const held = new Set(codes);
    const name = matchingPreset(codes, presets)?.name ?? codes.map(code => names.get(code) ?? code).join(', ');
    lenses.push({ label: translate('RELEASE_NOTES.PREVIEW.HOLDER', { name, n: count(held) }), held });
  });
  return lenses;
}
